import { inject } from "@angular/core";
import { CanMatchFn, Router } from "@angular/router";

import { environment } from "src/environments/environment";
import { OidcService } from "../oidc.service";
import { rememberReturnUrl } from "./return-url";

/**
 * Decides `/login` before the `LoginModule` chunk loads.
 *
 * `LoginComponent` used to make this call in `ngOnInit`, which meant a signed-out visitor
 * paid for the chunk, the branding request that rewrites the CSS custom properties and swaps
 * the favicon, and the loader overlay — all of it painting before the IdP redirect. A
 * `CanMatch` guard runs before the chunk is fetched and before any component exists, so the
 * handoff leaves the page blank.
 *
 * The portal form stays the fallback in both cases that need one: no issuer configured, and an
 * IdP that cannot be reached. Either lets the route match so `LoginComponent` renders
 * `rl-sign-in`. `/oidc-callback` is a separate top-level route and is never seen by this guard.
 */
export const loginOidcHandoffGuard: CanMatchFn = async (): Promise<boolean> => {
  const oidc = inject(OidcService);

  // `/login?returnUrl=` arriving from outside the portal (or from a guard bounce) is stashed
  // before any branch: the IdP round trip, the classic form and an already-stored session all
  // finish somewhere that takes it back out.
  rememberReturnUrl(inject(Router).currentNavigation()?.extractedUrl.queryParamMap.get("returnUrl"));

  if (!handoffConfigured() || hasStoredSession()) {
    return true;
  }

  try {
    await oidc.login();
  } catch {
    oidc.handoffFailed = true;
    return true;
  }

  // `login()` has already assigned `window.location.href`, so the document is on its way out.
  // Settling would either paint the form for the instant before unload (`true`) or leave the
  // router with nothing to match and raise NG04002 into Sentry on every sign-in (`false`), so
  // the navigation is deliberately left pending until the browser leaves.
  return new Promise<boolean>(() => undefined);
};

/**
 * Mirrors `LoginComponent.useOidc` exactly: an explicitly configured issuer is what turns the
 * handoff on, so environments without an IdP keep the classic portal login.
 */
function handoffConfigured(): boolean {
  return Boolean((environment as { oidcIssuer?: string }).oidcIssuer);
}

function hasStoredSession(): boolean {
  try {
    return localStorage.getItem("currentUser") !== null;
  } catch {
    return false;
  }
}
